import * as Sentry from '@sentry/react-native';
import type { MmkvStoreContract } from 'src/data/services/types/MmkvStorageTypes';

/**
 * Web sibling of MmkvStorage.ts. react-native-mmkv v4 is a Nitro/JSI module with
 * no web build, so in the browser the same store writes to localStorage. The
 * bundler resolves this file for platform === 'web' only.
 */

/** The raw string store this file writes through: localStorage, or the fallback below. */
type StringStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Used when localStorage cannot be reached. A browser may refuse it outright:
 * Safari in private mode, storage disabled by policy, or an embedded document
 * whose storage access was denied. Reaching for it then throws on the very first
 * read, which happens during startup, so the app would die before showing
 * anything. Losing the values is not free (the installation id is regenerated,
 * so the visit counts as a new device), but it beats not starting.
 */
function createMemoryStore(): StringStore {
	const values = new Map<string, string>();

	return {
		getItem: (key) => values.get(key) ?? null,
		setItem: (key, value) => {
			values.set(key, value);
		},
		removeItem: (key) => {
			values.delete(key);
		},
	};
}

let _store: StringStore | null = null;

function getInstance(): StringStore {
	if (_store) return _store;

	try {
		// Both lines can throw: reaching the property is what a blocked storage
		// refuses, and a read is what proves the store actually answers.
		const storage = window.localStorage;
		storage.getItem('__probe__');

		_store = storage;
	} catch (error) {
		Sentry.captureException(error, { tags: { context: 'mmkv', step: 'createInstance' } });
		_store = createMemoryStore();
	}

	return _store;
}

function readRaw(key: string): string | null {
	return getInstance().getItem(key);
}

let _reportedWriteFailure = false;

function writeRaw(key: string, value: string): void {
	try {
		getInstance().setItem(key, value);
	} catch (error) {
		// localStorage refuses writes once the origin's quota is full, where MMKV
		// would still have room. Dropping the value beats throwing at the caller,
		// which is a store action or a render. A full store stays full, so this is
		// reported once instead of on every write that follows.
		if (!_reportedWriteFailure) {
			_reportedWriteFailure = true;
			Sentry.captureException(error, { tags: { context: 'mmkv', step: 'set' } });
		}
	}
}

/** Only for tests: drops the cached store so the next call resolves again. */
export function resetMmkvStoreForTesting(): void {
	_store = null;
	_reportedWriteFailure = false;
}

export const MmkvStore: MmkvStoreContract = {
	set(key, value) {
		if (value === null) {
			getInstance().removeItem(key);
			return;
		}
		if (typeof value === 'object') {
			writeRaw(key, JSON.stringify(value));
		} else {
			writeRaw(key, String(value));
		}
	},

	get(key, def = null) {
		const value = readRaw(key);
		if (value === null || value === 'null') return def;
		return value;
	},

	getInt(key, def = null) {
		// localStorage keeps no types, so a number comes back as the string it went
		// in as; MMKV's getNumber does that conversion itself.
		const value = readRaw(key);
		if (value === null) return def;
		const parsed = Number(value);
		return Number.isNaN(parsed) ? def : parsed;
	},

	getBool(key) {
		const value = readRaw(key);
		if (value === 'true') return true;
		if (value === 'false') return false;
		// Absent, or stored as something other than a boolean. MMKV's typed read
		// returns undefined for both, which callers see as null.
		return null;
	},

	getObject<T = object>(key: string): T | null {
		const value = readRaw(key);
		if (!value || value === 'null') return null;
		try {
			return JSON.parse(value) as T;
		} catch {
			return null;
		}
	},

	remove(key) {
		getInstance().removeItem(key);
	},

	contains(key) {
		return readRaw(key) !== null;
	},
};
